import { Subject, of } from 'rxjs';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forEach, identity, map, pickBy } from 'lodash';
import { Component, ChangeDetectorRef, OnDestroy, Inject, PLATFORM_ID, Renderer2 } from '@angular/core';

import {
  CartProductType,
  CartProductInput,
  ShoppingCartType,
} from '@sifca-monorepo/ecommerce-generator';

import { OrdersService } from '../../services/order/order/order.service';
import { TranslateService } from '@ngx-translate/core';
import { AppService, AuthService, SeoService, SharedService } from '../../services';

@Component({
  template: '',
})
export class BaseCartComponent implements OnDestroy {
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  seo: any;
  totalPrice = 0;
  products: any[];
  posId: string = '';
  couponForm: FormGroup;
  successMessage: string;
  authenticated: boolean;
  priceBeforReduction = 0;
  quantityForm: FormGroup;
  isButtonDisabled = false;
  selectedSpecs: string[] = [];
  isCouponButtonDisabled = true;
  shoppingCart: ShoppingCartType;
  allProducts: CartProductInput[] = [];

  constructor(
    protected router: Router,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected appService: AppService,
    protected seoService: SeoService,
    protected titleTagService: Title,
    protected formBuilder: FormBuilder,
    protected authService: AuthService,
    protected orderService: OrdersService,
    protected translate: TranslateService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.authService.authenticated$.pipe(takeUntil(this.unsubscribeAll)).subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.changeDetectorRef.markForCheck();
    });
    this.couponForm = this.formBuilder.group({
      code: ['', Validators.required],
    });
    this.quantityForm = this.formBuilder.group({
      quantity: [1],
    });
    this.sharedService.posId$.pipe(takeUntil(this.unsubscribeAll)).subscribe((posId) => {
      this.posId = posId;
    });
    if (!this.authenticated) {
      this.appService.loadCart();
      this.appService.items$.pipe(takeUntil(this.unsubscribeAll)).subscribe((items: any[]) => {
        this.products = items;
        this.allProducts = [];
        forEach(this.products, (product) => {
          const value: any = {
            barcode: product.barcode.id,
            quantity: product.quantity,
          };
          this.allProducts = [...this.allProducts, value];
        });
        this.changeDetectorRef.markForCheck();
      });
      this.orderService.calculatedShoppingCart$.pipe(takeUntil(this.unsubscribeAll)).subscribe((shoppingCart: any) => {
        this.shoppingCart = shoppingCart;
        this.totalPrice = +shoppingCart?.price.gross.afterReduction;
      });
    } else {
      this.orderService.productCart$.pipe(takeUntil(this.unsubscribeAll)).subscribe((res) => {
        this.shoppingCart = res;
        this.priceBeforReduction = Number(res?.price.gross.beforeReduction);
        this.products = res?.products;
        this.totalPrice = +res?.price.gross.afterReduction;
        map(
          res?.products,
          (product) =>
            (this.quantityForm = this.formBuilder.group({
              quantity: [product.quantity],
            })),
        ),
          (this.couponForm = this.formBuilder.group({
            code: ['', Validators.required],
          }));
        this.quantityForm = this.formBuilder.group({
          quantity: [1],
        });
        this.couponForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
          this.isCouponButtonDisabled = false;
        });
      });
    }
    this.seoService
      .getGeneralSEOJSON()
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
          this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
            const link = this.renderer.createElement('link');
            if (seoLink.sizes) {
              link.sizes = seoLink.sizes;
            }
            if (seoLink.href) {
              link.href = seoLink.href;
            }
            if (seoLink.rel) {
              link.rel = seoLink.rel;
            }
            this.renderer.appendChild(this.document.head, link);
            return link;
          });
      });
  }

  calculateTotalPrice() {
    this.allProducts = [];
    forEach(this.products, (product) => {
      const value: any = {
        barcode: product.barcode.id,
        quantity: product.quantity,
      };
      this.allProducts = [...this.allProducts, value];
    });
  }

  removeProduct(product) {
    this.selectedSpecs = [];
    if (!this.authenticated) {
      this.allProducts = [];
      this.appService.removeItem(product);
      this.calculateTotalPrice();
    } else {
      const formValue = {
        product: product.product?.id,
        barcode: product.barcode?.id,
        quantity: product.quantity,
      };
      this.orderService
        .removeFromCart(this.posId, formValue)
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            this.changeDetectorRef.markForCheck();
          }
        });
    }
  }

  emptyTargetCart() {
    this.orderService.emptyTargetCart().subscribe();
  }

  addQuantity(product: CartProductType, quantity: number) {
    this.isButtonDisabled = true;
    if (!this.authenticated) {
      this.allProducts = [];
      this.appService.addQuantity(product);
      this.calculateTotalPrice();
      this.isButtonDisabled = false;
    } else {
      const orderProducts: any = {
        barcode: product.barcode.id,
      };
      this.orderService
        .addProductsToCart([orderProducts])
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            this.isButtonDisabled = false;
            this.changeDetectorRef.markForCheck();
          }
        });
    }
  }

  reduceQantity(product: CartProductType, quantity: number) {
    this.isButtonDisabled = true;
    if (!this.authenticated) {
      this.allProducts = [];
      this.appService.removeItem(product);
      this.calculateTotalPrice();
      this.isButtonDisabled = false;
    } else {
      const orderProducts: any = {
        barcode: product.barcode.id,
      };
      this.orderService
        .removeFromCart(this.posId, orderProducts)
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            this.isButtonDisabled = false;
            product.quantity = quantity > 0 ? quantity : 1;
            this.changeDetectorRef.markForCheck();
          }
        });
    }
  }

  isCouponValid() {
    this.orderService
      .isCouponValid(this.couponForm.value.code.trim())
      .pipe(
        catchError(() => {
          this.translate.get('SHARED.INVALID_COUPON').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          return of(null);
        }),
      )
      .subscribe((response: any) => {
        if (response?.isValid) {
          this.orderService.selectedCoupon = response?.coupon;
          this.translate.get('SHARED.COUPON_APPLIED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        } else {
          this.translate.get('SHARED.INVALID_COUPON').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  addDiscountToCart() {
    this.router.navigateByUrl('/checkout');
  }

  trackByFn(index: number, item: any) {
    if (!item) {
      return null;
    }
    return item.slug;
  }

  ngOnDestroy() {
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }
}
